/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, extendObservable } from 'mobx';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ModelType } from 'src/luis/types/ModelType';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { EntityStoreHouse } from 'src/luis/types/stores/entities/EntityStoreHouse';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { PatternAnyEntityStore } from 'src/luis/types/stores/entities/PatternAnyEntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A factory function for creating an entity store house,
 * which houses an array of entity stores.
 */
export const createEntityStoreHouse = (): EntityStoreHouse => {
    const resourcesStoreHouse = createResourceStoreHouse<EntityStore>();

    const entityStoreHouse: SubtractStore<EntityStoreHouse, ResourceStoreHouse<EntityStore>> = {
        /**
         * A computed function that filters the composite entities
         * from the array of entities.
         *
         * @returns An array of composite entities.
         */
        get compositeEntities() {
            return filterEntitiesByTypeId<CompositeEntityStore>(ModelType.Composite, this.items);
        },

        /**
         * A computed function that filters the Hierarchical entities
         * from the array of entities.
         *
         * @returns An array of composite entities.
         */
        get hierarchicalEntities() {
            return filterEntitiesByTypeId<HierarchicalEntityStore>(ModelType.Hierarchical, this.items);
        },

        /**
         * A computed function that filters the list entities
         * from the array of entities.
         *
         * @returns An array of list entities.
         */
        get listEntities() {
            return filterEntitiesByTypeId<ListEntityStore>(ModelType.List, this.items);
        },

        /**
         * A computed function that filters the pattern entities
         * from the array of entities.
         *
         * @returns An array of pattern entities.
         */
        get patternAnyEntities() {
            return filterEntitiesByTypeId<PatternAnyEntityStore>(ModelType.PatternAny, this.items);
        },

        /**
         * A computed function that filters the regex entities
         * from the array of entities.
         *
         * @returns An array of regex entities.
         */
        get regexEntities() {
            return filterEntitiesByTypeId<RegexEntityStore>(ModelType.Regex, this.items);
        },

        /**
         * A computed function that filters the simple entities
         * from the array of entities.
         *
         * @returns An array of simple entities.
         */
        get simpleEntities() {
            return filterEntitiesByTypeId<SimpleEntityStore>(ModelType.Simple, this.items);
        },

        /**
         * Finds an entity by the given name and returns undefined
         * if not found.
         *
         * @param name The name to search with.
         * @returns The entity or undefined if not found.
         */
        findByName(name) {
            return this.items.find(i => i.name === name);
        }
    };

    return extendObservable(
        resourcesStoreHouse,
        entityStoreHouse,
        {
            compositeEntities: computed,
            hierarchicalEntities: computed,
            listEntities: computed,
            patternAnyEntities: computed,
            regexEntities: computed,
            simpleEntities: computed,
            findByName: action
        },
        { deep: false }
    );
};

/**
 * A helper function to filter the given entities array
 * with the given type id.
 *
 * @param typeId The model type id to filter the entities array with.
 * @param entities The array to filter.
 * @returns An array of filtered entity stores by the given type id.
 */
const filterEntitiesByTypeId = <T extends EntityStore>(typeId: ModelType, entities: EntityStore[]): T[] =>
    <T[]>entities.filter(store => store.typeId === typeId);
